// Hand-written mirror of the job_run row (fraud-dbs
// 20260912000000_tables_jobs.sql, written by jupyter-fraud libs/tooling/
// jobs.py) and the small helpers that read it — in its own file so the
// brand dashboards' jobs card can share them without importing the Jobs
// page itself, the way autoDomainChips.tsx keeps the candidate chip out of
// the candidates dashboard.

import { parseDbTimestamp } from '@/lib/dates'

// 'paused': a queued run held back from the Jobs page; no worker claims it
// until it is resumed (fraud-dbs 20261015000000)
export type JobRunStatus = 'queued' | 'paused' | 'running' | 'succeeded' | 'failed' | 'cancelled'

export type JobRun = {
  id: string
  job_name: string
  platform_id: number | null
  // 'chain': queued by the worker that fired a schedule with a then_job, once
  // that firing succeeded -- the second half of a brand's daily block
  trigger: 'schedule' | 'manual' | 'chain'
  requested_by: string | null
  parent_run_id?: string | null
  schedule_id?: string | null
  // the run's own follow-up, queued by its worker when it succeeds (as a
  // schedule row's then_job is)
  then_job?: string | null
  then_params?: Record<string, any>
  params: Record<string, any>
  status: JobRunStatus
  worker: string | null
  queued_at: string
  started_at: string | null
  heartbeat_at: string | null
  finished_at: string | null
  // per_10m / eta_s: an analysis pass's own pace (analyses per ten minutes
  // so far, and what that makes of the rest), written by the worker
  progress: { step?: string; done?: number; total?: number; current?: string; message?: string; per_10m?: number; eta_s?: number; elapsed_s?: number } | null
  result: Record<string, any> | null
  error: string | null
  log_tail: string | null
  cancel_requested: boolean
  // a running run asked to stand still at its next checkpoint (Pause on the
  // board); paused_at is written by the job once it actually is, so the
  // board says "pausing" in between
  pause_requested: boolean
  paused_at: string | null
}

export type RunsResponse = { runs: JobRun[]; total: number; page: number; hasMore: boolean }

export const STATUS_TONE: Record<JobRunStatus, 'sky' | 'amber' | 'emerald' | 'red' | 'neutral'> = {
  queued: 'amber', paused: 'neutral', running: 'sky', succeeded: 'emerald', failed: 'red', cancelled: 'neutral',
}

// The worker heartbeats a running row every progress write; past this the
// row is shown as stalled (the worker's own sweep fails it after 5 min).
export const RUN_STALE_MS = 2 * 60 * 1000

export function isStale(run: JobRun) {
  if (run.status !== 'running') return false
  const d = parseDbTimestamp(run.heartbeat_at ?? run.started_at ?? undefined)
  return !d || Date.now() - d.getTime() > RUN_STALE_MS
}

export function fmtDuration(from?: string | null, to?: string | null) {
  const a = parseDbTimestamp(from ?? undefined)
  if (!a) return '—'
  const b = to ? parseDbTimestamp(to) : new Date()
  if (!b) return '—'
  const s = Math.max(0, Math.round((b.getTime() - a.getTime()) / 1000))
  if (s < 60) return `${s}s`
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`
}

// A number of seconds still to go, in the shape the worker's own messages
// use ('45s', '12m', '1h05'): the running row's "~1h05 left".
export function fmtLeft(seconds: number) {
  const s = Math.max(0, Math.round(seconds))
  if (s < 60) return `${s}s`
  if (s < 3600) return `${Math.floor(s / 60)}m`
  return `${Math.floor(s / 3600)}h${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}`
}

export function fmtAgo(iso?: string | null) {
  const d = parseDbTimestamp(iso ?? undefined)
  if (!d) return '—'
  const s = Math.max(0, Math.round((Date.now() - d.getTime()) / 1000))
  if (s < 60) return `${s}s ago`
  if (s < 3600) return `${Math.floor(s / 60)}m ago`
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`
  return `${Math.floor(s / 86400)}d ago`
}

// Which Verify button of the candidate detail queued a run, read from what
// the run already carries (so it works for every past run too): the
// /autodomains/analyze route queues the lane's job with the candidate_ids it
// was asked for and a mobile_engine (jupyter-fraud libs/backend/routes/
// autodomains.py LANE_JOBS). A scheduled or chained pass has no
// candidate_ids. Runs from before 2026-10-05 have no mobile_engine: the
// spoofed iPhone UA was the only mobile pass then. The labels are the
// buttons' own (DomainCandidateDetail).
export function verifyRunLabel(run: { job_name: string; mobile_engine?: string | null; has_candidate_ids: boolean }): string | null {
  if (!run.has_candidate_ids) return null
  const mobile = run.mobile_engine === 'webkit_iphone17' ? 'iPhone' : 'old mobile'
  if (run.job_name === 'auto_domains.analyze_5g') return `Verify on 5G (${mobile})`
  if (run.job_name === 'auto_domains.analyze') return `Verify via proxy (${mobile})`
  return null
}

export function verifyRunLabelOf(run: Pick<JobRun, 'job_name' | 'params'>): string | null {
  const ids = run.params?.candidate_ids
  return verifyRunLabel({ job_name: run.job_name, mobile_engine: run.params?.mobile_engine, has_candidate_ids: Array.isArray(ids) && ids.length > 0 })
}

// The job's name as a person reads it, for the board's rows and cards; the
// raw name (auto_domains.analyze_5g) stays beside it in mono. A job not
// listed shows its raw name.
const JOB_LABELS: Record<string, string> = {
  'auto_domains.analyze_5g': 'Domain analysis · 5G',
  'auto_domains.analyze': 'Domain analysis · Azure',
  'auto_domains.source': 'Domain discovery',
  'auto_domains.screen': 'Candidate screen',
  'auto_domains.explain_chain': 'Explain chain',
  'auto_domains.suggest_vocabulary': 'Vocabulary suggestions',
  'ctt_entries.update': 'CTT entries update',
}
export function jobLabel(name: string): string {
  return JOB_LABELS[name] ?? name
}
